import type { PermissionKey } from "./permission-keys.js";

// Keys each screen route needs, from the audited legacy route table (erp-audit/evidence/menu.txt) with the
// deviations the approved prototype adopted (remaining-issues.md item 7). The web app uses this to filter the
// menu and show the Access denied page; it is a convenience only. The server checks its own keys on every
// API request and never trusts this map.
type Need = PermissionKey[];
const K = (...k: PermissionKey[]): Need => k;

const MONEY_ROUTES = "Collect|Payment|Deposit|Withdraw|Expense|Salary|Loan|Reward|Deduction";
const REP_INV: Record<string, PermissionKey> = { Sale: "r3sale", SaleRefund: "r3saleRefund", Purchase: "r3purchase", PurchaseRefund: "r3purchaseRefund" };
const REP_MONEY: Record<string, PermissionKey> = { Collect: "r5collect", Payment: "r5payment", Deposit: "r5collect", Withdraw: "r5payment", Expense: "r6expense", Salary: "r4salary", Loan: "r4loan", Reward: "r4reward", Deduction: "r4deduction" };
const INV_VIEW: Record<string, PermissionKey> = {
  Sale: "invoiceSales", Quotation: "invoiceSales", SaleRefund: "invoiceSaleRefunds", Purchase: "invoicePurchases", PurchaseRefund: "invoicePurchaseRefunds",
  BeginningInventory: "invoiceBeginningInventories", Transfer: "invoiceTransfers", Inventory: "invoiceInventories", Corrupted: "invoiceCorrupteds"
};

const TABLE: [RegExp, (m: RegExpMatchArray) => Need | "anyReport"][] = [
  [/^\/(home|index)$/, () => K("home")],
  // The editors and the document page of every invoice kind: the keys depend on ?type= or on the document, so the
  // screen checks them (stock documents: STOCK_KEYS) and the server checks them again on every request.
  [/^\/invoices\/create$|^\/warehouses\/(inventory|transfers|depreciation)\/new$/, () => K()],
  [/^\/invoices\/(\w+)$/, (m) => (INV_VIEW[m[1]!] ? [INV_VIEW[m[1]!]!] : K("invoiceSales", "invoiceSaleAdd"))],
  [/^\/(Customer|Supplier)(\/[^/]+)?$/, () => K("accounts")],
  [new RegExp(`^/receipts/(${MONEY_ROUTES})$|^/Expense$`), () => K("monies")],
  [/^\/receipt\/[^/]+$/, () => K("monies")],
  [/^\/transfers$|^\/transfer\/[^/]+$/, () => K("transfers")],
  [/^\/partners$/, () => K("partners")],
  [/^\/journals$|^\/journals\/show\/[^/]+$/, () => K("journalEntries")],
  [/^\/legacy-history$|^\/legacy-history\/[^/]+$/, () => K("journalEntries")],
  [/^\/journals\/create$/, () => K("journalEntries", "journalEntryAdd")],
  [/^\/journals\/edit\/[^/]+$/, () => K("journalEntries", "journalEntryUpdate")],
  [/^\/items\/allItems$|^\/items\/show\/[^/]+$/, () => K("items")],
  [/^\/items\/create$|^\/items\/import$/, () => K("items", "itemAdd")],
  [/^\/items\/edit\/[^/]+$/, () => K("items", "itemUpdate")],
  [/^\/items\/qrPrint$/, () => K("itemUnitBarcodePrint")],
  [/^\/item-Category$/, () => K("itemCategories")],
  [/^\/unitsConfig$/, () => K("itemUnits")],
  [/^\/item-kinds$/, () => K("itemKinds")],
  [/^\/reports$/, () => "anyReport"],
  [/^\/reports\/general-ledger$/, () => K("r1generalLedger")],
  [/^\/reports\/balance-trial\/\w+$/, () => K("r1balanceTrial")],
  [/^\/reports\/charts-statement$/, () => K("r1chartOfAccountLevel3Statment")],
  [/^\/reports\/account-statement$/, () => K("r5accountStatement")],
  [/^\/reports\/accounts$/, () => K("r5accounts")],
  [/^\/reports\/journals-report$/, () => K("r6journals")],
  [/^\/reports\/journals-statement$/, () => K("r6journalStatment")],
  [/^\/reports\/all-invoices\/(\w+)$/, (m) => [REP_INV[m[1]!] ?? "r3sale"]],
  [/^\/reports\/money\/(\w+)$/, (m) => [REP_MONEY[m[1]!] ?? "r5collect"]],
  [/^\/reports\/chart-of-accounts$/, () => K("r1chartOfAccountLevel3")],
  [/^\/reports\/income-statement$/, () => K("r2incomeStatement")],
  [/^\/reports\/balance-sheet$/, () => K("r2balanceSheet")],
  // Correction pass 2026-10-27: the cash flow statement and its indicators have no audited key; the balance sheet's applies.
  [/^\/reports\/cash-flow(-indicators|\/classification)?$/, () => K("r2balanceSheet")],
  // Receipts and payments of every kind: all three money report keys (assumption, as the audit names no key).
  [/^\/reports\/receipt-payment$/, () => K("r5collect", "r5payment", "r6expense")],
  [/^\/reports\/invoices-details$/, () => K("r3details")],
  [/^\/reports\/invoices-profits$/, () => K("r3profits")],
  [/^\/reports\/transfers$/, () => K("r6transfer")],
  [/^\/reports\/movement-summary$/, () => K("r6movementSummary")],
  [/^\/reports\/tax\/(\w+)$/, (m) => K(m[1] === "Purchase" ? "r7purchase" : "r7sale")],
  [/^\/reports\/asset-tax$/, () => K("r7asset")],
  [/^\/reports\/tax-return$/, () => K("r7taxReturn")],
  [/^\/reports\/tobacco-fees$/, () => K("r7taxReturn")],
  [/^\/reports\/warehouse$/, () => K("r8warehouseBalance")],
  [/^\/reports\/warehouses-items$/, () => K("r8warehouseItems")],
  [/^\/reports\/item-movement$/, () => K("r8warehouseItems")],
  [/^\/hr\/employees$/, () => K("staffers")],
  [/^\/hr\/allowances$/, () => K("allowances")],
  [/^\/assets\/all$/, () => K("assets")],
  [/^\/assets\/types$/, () => K("assetKinds")],
  [/^\/assets\/purchases$/, () => K("assetInvoices")],
  [/^\/assets\/depreciation$/, () => K("assetDepreciations")],
  [/^\/assets\/new-purchase$/, () => K("assetInvoices", "assetInvoiceAdd")],
  [/^\/assets-purchase\/show\/[^/]+$/, () => K("assetInvoices")],
  [/^\/reports\/all-assets$/, () => K("r9assets")],
  [/^\/reports\/asset-depreciation$/, () => K("r9depreciation")],
  [/^\/reports\/asset-invoices$/, () => K("r9purchase")],
  [/^\/reports\/asset-invoice-details$/, () => K("r9details")],
  [/^\/warehouses\/all$|^\/warehouse-stock$/, () => K("warehouses")],
  [/^\/settings\/organization$/, () => K("setting")],
  [/^\/settings\/period-lock$/, () => K("setting")],
  [/^\/settings\/expense-types$/, () => K("setting")],
  [/^\/settings\/tobacco$/, () => K("setting")],
  [/^\/settings\/pos-payment-buttons$/, () => K("setting")],
  // Posting policies (migration 034): every user sees the policies; the server decides who changes them or posts.
  [/^\/settings\/posting$/, () => K()],
  // Chart templates (migration 037): the chart's or the settings' keys to see; the server lets only the owner install.
  [/^\/settings\/chart-template$/, () => K()],
  // Price controls (migration 039): everyone sees their own rights and requests; the server decides the rest.
  [/^\/price-controls$/, () => K()],
  [/^\/settings\/branches$/, () => K("stores")],
  [/^\/branch-barcode\/[^/]+$/, () => K("stores", "storeBarcode")],
  [/^\/settings\/treasury$/, () => K("journals")],
  [/^\/settings\/accountIndex$|^\/account\/[^/]+$/, () => K("chartOfAccountLevel3s")],
  [/^\/settings\/accountTypes$/, () => K("chartOfAccountLevel2s")],
  [/^\/cost-centers$/, () => K("costCenters")],
  [/^\/settings\/tax$/, () => K("taxTypes")],
  // Hidden legacy route, opened from the reports hub and the accounts index (menu.txt: r1chartOfAccountsTree).
  [/^\/chart$/, () => K("r1chartOfAccountsTree")],
  [/^\/printers$/, () => K("printers")],
  [/^\/users-settings$/, () => K("users")],
  [/^\/permission\/[^/]+$/, () => K("users", "userPrivileges")],
  [/^\/client-area$/, () => K("clientArea")],
  // Settings › Package (migration 043): the business's package, dates, limits and requests to Delta.
  [/^\/settings\/package$/, () => K("clientArea")],
  // POS: the POS audit shows no separate keys, so the invoice keys apply (assumption, remaining-issues.md item 9).
  [/^\/pos$|^\/pos\/invoices$/, () => K("invoiceSales")],
  [/^\/pos\/shifts\/[^/]+$/, () => K("invoiceSales")],
  [/^\/pos\/sale$/, () => K("invoiceSales", "invoiceSaleAdd")],
  [/^\/pos\/refund$/, () => K("invoiceSaleRefunds", "invoiceSaleRefundAdd")],
  [/^\/pos\/reports$/, () => K("r3sale")],
  // This workstation's printers (phase 12a): seeing them needs the printers key; changing a mapping, printerUpdate too.
  [/^\/pos\/printers$|^\/printers\/this-pc$/, () => K("printers")]
];

// HR is excluded from Delta (owner's decision; docs/outstanding-work.md "Scope: HR"): a separate HR system is
// integrated later. These audited HR routes are left out of the menu and show a "not part of Delta" page instead of
// a placeholder. Their permission keys stay in the 190-key model, and accounting history that came from HR vouchers
// stays in the general accounts.
const EXCLUDED_HR = /^\/hr(\/|$)|^\/employee\/show\/|^\/receipts\/(Salary|Loan|Reward|Deduction)$|^\/reports\/(employees|employee-statement|salaries)$|^\/reports\/money\/(Salary|Loan|Reward|Deduction)$/;

/** True for an audited route that is outside Delta's scope (HR). */
export function isExcludedRoute(path: string): boolean {
  return EXCLUDED_HR.test(path);
}

export interface AccessSubject { administrator: boolean; permissions: readonly string[] }

/** Keys the subject is missing for a route; "r*" means "any report key". Unknown routes need nothing. */
export function missingForRoute(path: string, s: AccessSubject): string[] {
  if (s.administrator) return [];
  const has = new Set(s.permissions);
  for (const [re, fn] of TABLE) {
    const m = path.match(re);
    if (!m) continue;
    const need = fn(m);
    if (need === "anyReport") return s.permissions.some((k) => /^r\d/.test(k)) ? [] : ["r*"];
    return need.filter((k) => !has.has(k));
  }
  return [];
}
